
if (!customElements.get('ss-product-bundle')) {
  class SSProductBundle extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._handlers = [];

      this.recommended = parseInt(this.dataset.recommended, 10) || 1;
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.discounts = this._parseJSON(this.querySelector('.ssb__discounts')) || [];

      this.cards = Array.from(this.querySelectorAll('.ssb__card'));
      this.items = Array.from(this.querySelectorAll('.ssb__item'));
      this.progressBar = this.querySelector('.ssb__progress');
      this.progressFill = this.querySelector('.ssb__progress-fill');
      this.elSubtotal = this.querySelector('.ssb__subtotal');
      this.elDiscount = this.querySelector('.ssb__discount');
      this.elTotal = this.querySelector('.ssb__total');
      this.buyBtn = this.querySelector('.ssb__buy');
      this._buyHref = '';

      this.cards.forEach((card) => this._initCard(card));

      // Delegated remove (one listener for the whole panel).
      this._on(this, 'click', (e) => {
        const removeBtn = e.target.closest('.ssb__item-remove');
        if (removeBtn) this._removeItem(removeBtn.closest('.ssb__item'));
      });

      this._on(this.buyBtn, 'click', () => {
        if (this._buyHref) window.location.href = this._buyHref;
      });

      this._recalc();
    }

    disconnectedCallback() {
      (this._handlers || []).forEach(([el, ev, fn]) => el.removeEventListener(ev, fn));
      this._handlers = [];
      this._bound = false;
    }

    _on(el, ev, fn) {
      if (!el) return;
      el.addEventListener(ev, fn);
      this._handlers.push([el, ev, fn]);
    }

    _parseJSON(node) {
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    _initCard(card) {
      card._variants = this._parseJSON(card.querySelector('.ssb__variants')) || [];
      card._img = card.querySelector('.ssb__card-img');
      card._priceCurrent = card.querySelector('.ssb__card-price-current');
      card._priceCompare = card.querySelector('.ssb__card-price-compare');
      card._addBtn = card.querySelector('.ssb__add');
      card._optionGroups = Array.from(card.querySelectorAll('.ssb__option'));

      card.querySelectorAll('.ssb__swatch').forEach((swatch) => {
        this._on(swatch, 'click', () => {
          const group = swatch.closest('.ssb__option');
          if (!group) return;
          group.querySelectorAll('.ssb__swatch').forEach((s) => {
            s.classList.remove('is-active');
            s.setAttribute('aria-pressed', 'false');
          });
          swatch.classList.add('is-active');
          swatch.setAttribute('aria-pressed', 'true');
          this._updateCard(card);
        });
      });

      this._on(card._addBtn, 'click', () => this._addItem(card));
      this._updateCard(card);
    }

    _selectedValues(card) {
      const values = [];
      card._optionGroups.forEach((group) => {
        const pos = parseInt(group.dataset.position, 10);
        const active = group.querySelector('.ssb__swatch.is-active');
        if (active && pos) values[pos - 1] = active.dataset.value;
      });
      return values;
    }

    _currentVariant(card) {
      const selected = this._selectedValues(card);
      if (!selected.length) return card._variants[0];
      return (
        card._variants.find(
          (v) =>
            Array.isArray(v.options) &&
            v.options.length === selected.length &&
            v.options.every((o, i) => o === selected[i])
        ) || card._variants[0]
      );
    }

    _itemFor(card) {
      const bundle = card.dataset.bundle;
      return this.items.find((i) => i.dataset.bundle === bundle);
    }

    _isAdded(card) {
      const item = this._itemFor(card);
      return !!item && !item.hidden;
    }

    _isUnlocked(card) {
      const idx = this.cards.indexOf(card);
      if (idx <= 0) return true;
      return this._isAdded(this.cards[idx - 1]);
    }

    _updateCard(card) {
      const variant = this._currentVariant(card);
      if (!variant) return;
      card._current = variant;

      if (card._img && variant.image) card._img.src = variant.image;
      if (card._priceCurrent && variant.price) card._priceCurrent.innerHTML = variant.price;
      if (card._priceCompare) {
        if (variant.has_compare && variant.compare) {
          card._priceCompare.innerHTML = variant.compare;
          card._priceCompare.style.display = '';
        } else {
          card._priceCompare.style.display = 'none';
        }
      }

      const btn = card._addBtn;
      if (!btn) return;
      const item = this._itemFor(card);
      const added = !!item && !item.hidden && String(item.dataset.id) === String(variant.id);
      const available = variant.available === true;

      const unlocked = this._isUnlocked(card);
      const locked = available && !added && !unlocked;
      btn.disabled = !available || added || !unlocked;
      btn.classList.toggle('is-added', added || locked);
      btn.textContent = !available
        ? btn.dataset.soldLabel || 'Sold Out'
        : added
        ? btn.dataset.addedLabel || btn.dataset.addLabel || 'Added'
        : btn.dataset.addLabel || 'Add to Bundle';
    }

    _addItem(card) {
      const variant = card._current;
      if (!variant || !variant.available) return;
      const item = this._itemFor(card);
      if (!item) return;

      item.dataset.id = variant.id;
      item.dataset.handle = variant.handle || '';
      item.dataset.price = variant.price_num;
      const titleEl = item.querySelector('.ssb__item-title');
      const priceEl = item.querySelector('.ssb__item-price');
      const imgEl = item.querySelector('.ssb__item-img');
      if (titleEl) titleEl.textContent = card.dataset.title || '';
      if (priceEl) priceEl.innerHTML = variant.price || '';
      if (imgEl && variant.image) imgEl.src = variant.image;
      const removeBtn = item.querySelector('.ssb__item-remove');
      if (removeBtn) removeBtn.setAttribute('aria-label', `Remove ${card.dataset.title || 'item'} from bundle`);
      item.hidden = false;

      this.cards.forEach((c) => this._updateCard(c));
      this._recalc();
    }

    _removeItem(item) {
      if (!item) return;
      item.hidden = true;
      delete item.dataset.id;
      delete item.dataset.handle;
      delete item.dataset.price;

      this.cards.forEach((c) => this._updateCard(c));
      this._recalc();
    }
    _percentFor(handle) {
      for (const d of this.discounts) {
        if (d.type === 'percentage' && Array.isArray(d.products) && d.products.includes(handle)) {
          return d.value;
        }
      }
      return null;
    }

    _containsAll(have, need) {
      const count = (arr) => arr.reduce((acc, v) => ((acc[v] = (acc[v] || 0) + 1), acc), {});
      const c = count(have);
      const n = count(need);
      return Object.keys(n).every((k) => (c[k] || 0) >= n[k]);
    }

    _money(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }

    _recalc() {
      const active = this.items.filter((i) => !i.hidden);
      let subtotal = 0;
      let total = 0;
      const handles = [];
      const variantParts = [];

      active.forEach((item) => {
        const num = parseInt(item.dataset.price, 10) || 0;
        const handle = item.dataset.handle || '';
        handles.push(handle);
        variantParts.push(`${item.dataset.id}:1`);
        const pct = this._percentFor(handle);
        subtotal += num;
        total += pct != null ? num * pct : num;
      });

      this.discounts.forEach((d) => {
        if (d.type !== 'percentage' && this._containsAll(handles, d.products || [])) {
          total -= d.value;
        }
      });
      if (total < 0) total = 0;
      const discount = subtotal - total;

      if (this.elSubtotal) this.elSubtotal.innerHTML = this._money(subtotal);
      if (this.elDiscount) this.elDiscount.innerHTML = this._money(discount);
      if (this.elTotal) this.elTotal.innerHTML = this._money(total);

      const pct = Math.min(100, (active.length / this.recommended) * 100);
      if (this.progressFill) this.progressFill.style.width = `${pct}%`;
      if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(Math.round(pct)));

      if (this.buyBtn) {
        if (active.length === 0) {
          this.buyBtn.disabled = true;
          this._buyHref = '';
        } else {
          this.buyBtn.disabled = false;
          this._buyHref = `${this.cartUrl}/${variantParts.join(',')}`;
        }
      }
    }
  }

  customElements.define('ss-product-bundle', SSProductBundle);
}
